import React, { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { searchAddresses } from "../utils/geoApi";

const INPUT_CLASS = "w-full h-11 px-3.5 rounded-xl border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8] bg-white";

/**
 * Free-text street-address autocomplete (e.g. company HQ). Biased by whatever
 * country/region/city is already known. Selecting a suggestion saves the
 * display address + lat/lng; typing without selecting one still saves the
 * raw text (no forced match), same "manual override always allowed" pattern
 * as LocationSelector's city field.
 */
export function AddressAutocomplete({ value, countryCode, admin1Name, city, onChange, placeholder, testId }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const handleInput = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange({ address: text, latitude: null, longitude: null });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchAddresses(text, countryCode, admin1Name, city);
        setSuggestions(results || []);
        setShowDropdown(true);
      } catch {
        setSuggestions([]);
        setShowDropdown(true);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const pickSuggestion = (s) => {
    setQuery(s.display_name);
    setShowDropdown(false);
    setSuggestions([]);
    onChange({ address: s.display_name, latitude: s.lat, longitude: s.lon });
  };

  return (
    <div className="relative">
      <input
        data-testid={testId}
        className={INPUT_CLASS}
        value={query}
        onChange={handleInput}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3.5 text-slate-400" />}
      {showDropdown && !searching && suggestions.length > 0 && (
        <ul data-testid={`${testId}-suggestions`} className="absolute z-30 mt-1 w-full bg-white border border-[#E2E8F0] rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li
              key={i}
              data-testid={`${testId}-suggestion-${i}`}
              onClick={() => pickSuggestion(s)}
              className="px-3.5 py-2.5 text-sm hover:bg-[#F0F7FF] cursor-pointer flex items-start gap-2"
            >
              <MapPin className="w-4 h-4 text-[#028CF8] shrink-0 mt-0.5" />
              <span>{s.display_name}</span>
            </li>
          ))}
        </ul>
      )}
      {showDropdown && !searching && suggestions.length === 0 && query.trim().length >= 3 && (
        <div data-testid={`${testId}-no-results`} className="absolute z-30 mt-1 w-full bg-white border border-[#E2E8F0] rounded-xl shadow-lg px-3.5 py-2.5 text-sm text-slate-400">
          Sin sugerencias — podés seguir escribiendo la dirección manualmente.
        </div>
      )}
    </div>
  );
}
